import React from 'react';

import { WIDGET_ANIMATION_DURATION_MS } from 'widget/consts/widget';

type AnimationPhase = 'hidden' | 'entering' | 'visible' | 'exiting';

/**
 * Управляет mount/unmount элемента с CSS-анимацией.
 * При isVisible=true: mount → entering (opacity 0) → visible (opacity 1).
 * При isVisible=false: exiting (opacity 0) → unmount после завершения transition.
 * Прерывание безопасно: CSS transition интерполирует от текущего значения.
 */
export function useAnimatedVisibility(isVisible: boolean): {
  shouldRender: boolean;
  isAnimatedIn: boolean;
} {
  const [phase, setPhase] = React.useState<AnimationPhase>(
    isVisible ? 'visible' : 'hidden'
  );
  const isFirstRender = React.useRef(true);
  const targetRef = React.useRef(isVisible);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = React.useRef<number | null>(null);

  const clearTimeoutsAndRAFs = React.useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    targetRef.current = isVisible;

    clearTimeoutsAndRAFs();

    if (isVisible) {
      setPhase('entering');
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => {
          if (targetRef.current) {
            setPhase('visible');
          }
        });
      });
    } else {
      setPhase('exiting');
      timerRef.current = setTimeout(() => {
        if (!targetRef.current) {
          setPhase('hidden');
        }
      }, WIDGET_ANIMATION_DURATION_MS);
    }

    return clearTimeoutsAndRAFs;
  }, [isVisible, clearTimeoutsAndRAFs]);

  return {
    shouldRender: phase !== 'hidden',
    isAnimatedIn: phase === 'visible'
  };
}
